import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '@/lib/index';
import { productsData, companyStatsData } from '@/data/index';
import { StatCard } from '@/components/Cards';
import { IMAGES } from '@/assets/images';
import { Factory, ShieldCheck, Globe, FlaskConical, ArrowRight, ChevronDown } from 'lucide-react';

const ORANGE = 'oklch(0.66 0.19 42)';
const DARK_BG = '#F7F8FA';   /* 浅色背景 */

function getProductRoute(id: string): string {
  if (id === 'fkm') return '/products/fkm';
  if (id === 'epdm') return '/products/epdm';
  if (id === 'nbr') return '/products/nbr';
  if (id === 'nr') return '/products/nr';
  if (id === 'cr') return '/products/cr';
  if (id === 'antistatic') return '/products/esd';
  return '/products';
}
const CARD_BG = '#FFFFFF';
const BORDER = '#D0D5E2';
const MUTED_TEXT = '#5A6780';

export default function Home() {
  const advantages = [
    {
      icon: Factory,
      title: '20+年经验',
      titleEn: '20+ YEARS',
      description: '河北省沧州市橡胶产业基地，稳定运营逾二十年',
    },
    {
      icon: ShieldCheck,
      title: 'ISO9001认证',
      titleEn: 'ISO 9001',
      description: '通过国际质量管理体系认证，全流程质控',
    },
    {
      icon: Globe,
      title: '全球出口',
      titleEn: 'GLOBAL EXPORT',
      description: '产品出口欧美澳日韩等10个国家和地区',
    },
    {
      icon: FlaskConical,
      title: '定制配方',
      titleEn: 'CUSTOM FORMULA',
      description: '资深工程师团队提供材料选型与定制开发',
    },
  ];

  return (
    <div className="w-full" style={{ background: DARK_BG }}>
      <section
        className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden"
        style={{ background: DARK_BG }}
      >
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.FACTORY_2}
            alt="Factory Background"
            className="w-full h-full object-cover"
            style={{ opacity: 0.3, filter: 'brightness(0.7) contrast(1.2) saturate(0.6)' }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(160deg,
                rgba(15,30,53,0.80) 0%,
                rgba(15,30,53,0.55) 50%,
                rgba(15,30,53,0.82) 100%
              )`,
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background: `radial-gradient(ellipse 60% 50% at 15% 60%, oklch(0.66 0.19 42 / 0.08) 0%, transparent 60%)`,
            }}
          />
        </div>

        <div
          className="absolute inset-0 z-0 opacity-10"
          style={{
            backgroundImage: `linear-gradient(oklch(0.66 0.19 42 / 0.15) 1px, transparent 1px), linear-gradient(90deg, oklch(0.66 0.19 42 / 0.15) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
            pointerEvents: 'none',
          }}
        />

        <div className="relative z-10 container mx-auto px-4 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <span
              className="inline-flex items-center gap-2 text-xs font-mono font-bold tracking-[0.25em] uppercase px-4 py-2 rounded-sm"
              style={{
                color: ORANGE,
                border: `1px solid oklch(0.66 0.19 42 / 0.4)`,
                background: 'oklch(0.66 0.19 42 / 0.08)',
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: ORANGE }}
              />
              POLY-PRIME INDUSTRIAL TECHNOLOGY
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="max-w-5xl mx-auto mb-6"
          >
            <h1
              className="text-5xl md:text-7xl font-black leading-tight tracking-tight mb-3"
              style={{ color: '#FFFFFF' }}
            >
              特种橡胶<span style={{ color: ORANGE }}>·</span>工业密封
            </h1>
            <p
              className="text-base md:text-lg font-mono tracking-[0.3em] uppercase"
              style={{ color: 'rgba(255,255,255,0.75)' }}
            >
              SPECIAL RUBBER · INDUSTRIAL SEALING SOLUTIONS
            </p>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-base md:text-lg max-w-2xl mx-auto mb-12 leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.80)' }}
          >
            专注工业橡胶密封材料研发与制造，服务化工石化、建筑幕墙、汽车轨道交通、矿山采掘等多个工业领域
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-12"
          >
            {companyStatsData.map((stat, index) => (
              <motion.div
                key={stat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <StatCard stat={stat} />
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Link
              to={ROUTE_PATHS.PRODUCTS}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm transition-all"
              style={{
                background: ORANGE,
                color: '#FFFFFF',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'oklch(0.72 0.20 42)';
                (e.currentTarget as HTMLElement).style.boxShadow = `0 4px 24px oklch(0.66 0.19 42 / 0.4)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = ORANGE;
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              查看产品系列
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={ROUTE_PATHS.CONTACT}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm transition-all"
              style={{
                color: '#FFFFFF',
                border: `1px solid rgba(255,255,255,0.45)`,
                background: 'transparent',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = ORANGE;
                (e.currentTarget as HTMLElement).style.color = ORANGE;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.45)';
                (e.currentTarget as HTMLElement).style.color = '#FFFFFF';
              }}
            >
              联系技术工程师
            </Link>
          </motion.div>
        </div>

        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          style={{ color: 'rgba(255,255,255,0.6)' }}
        >
          <ChevronDown className="w-6 h-6" />
        </motion.div>
      </section>

      <section className="py-24" style={{ background: '#F0F2F5' }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-0.5" style={{ background: ORANGE }} />
              <span
                className="text-xs font-mono font-bold tracking-[0.25em] uppercase"
                style={{ color: ORANGE }}
              >
                PRODUCT LINES
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-foreground mb-3">
              产品系列
            </h2>
            <p style={{ color: MUTED_TEXT }} className="text-base max-w-2xl">
              六大核心橡胶品种，覆盖工业密封全场景
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {productsData.map((product, index) => {
              const detailRoute = getProductRoute(product.id);
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                >
                  <Link
                    to={detailRoute}
                    className="group relative overflow-hidden rounded p-6 transition-all duration-300 flex flex-col h-full block"
                    style={{ background: CARD_BG, border: `1px solid ${BORDER}`, textDecoration: 'none' }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = ORANGE;
                      (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 24px rgba(230,115,0,0.15)`;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.borderColor = BORDER;
                      (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                    }}
                  >
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{
                        background: `radial-gradient(circle at 50% 0%, oklch(0.66 0.19 42 / 0.05) 0%, transparent 60%)`,
                        pointerEvents: 'none',
                      }}
                    />
                    <div className="relative z-10">
                      <span
                        className="inline-block text-xs font-mono font-bold tracking-[0.05em] px-2.5 py-1 rounded-sm mb-4"
                        style={{
                          color: ORANGE,
                          background: 'rgba(230,115,0,0.10)',
                          border: '1px solid rgba(230,115,0,0.3)',
                        }}
                      >
                        {product.code}
                      </span>
                      <h3 className="text-xl font-bold text-foreground mb-2">{product.name}</h3>
                      <p
                        className="text-xs font-mono tracking-wider uppercase mb-4"
                        style={{ color: MUTED_TEXT }}
                      >
                        {product.nameEn}
                      </p>
                      <div
                        className="flex items-center gap-2 text-sm font-mono mb-3 pb-3"
                        style={{ borderBottom: `1px solid ${BORDER}` }}
                      >
                        <span style={{ color: MUTED_TEXT }}>温度范围:</span>
                        <span className="font-bold text-foreground">{product.tempRange}</span>
                      </div>
                      <p className="text-sm leading-relaxed mb-4" style={{ color: MUTED_TEXT }}>
                        {product.description}
                      </p>
                      <div
                        className="inline-flex items-center gap-2 text-sm font-bold transition-colors"
                        style={{ color: ORANGE }}
                      >
                        查看详情
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <Link
              to={ROUTE_PATHS.PRODUCTS}
              className="inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold rounded-sm transition-all"
              style={{
                background: ORANGE,
                color: '#FFFFFF',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'oklch(0.72 0.20 42)';
                (e.currentTarget as HTMLElement).style.boxShadow = `0 4px 24px oklch(0.66 0.19 42 / 0.4)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = ORANGE;
                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
              }}
            >
              查看全部产品
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24" style={{ background: '#F7F8FA' }}>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-0.5" style={{ background: ORANGE }} />
              <span
                className="text-xs font-mono font-bold tracking-[0.25em] uppercase"
                style={{ color: ORANGE }}
              >
                WHY CHOOSE US
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-foreground mb-3">
              为什么选择我们
            </h2>
            <p style={{ color: MUTED_TEXT }} className="text-base max-w-2xl">
              20年专注橡胶研发，ISO 9001认证，全球出口，技术可靠
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {advantages.map((adv, index) => {
              const Icon = adv.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className="group relative overflow-hidden rounded p-6 flex flex-col items-center text-center transition-all duration-300"
                  style={{ background: CARD_BG, border: `1px solid ${BORDER}` }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = ORANGE;
                    (e.currentTarget as HTMLElement).style.boxShadow = `0 8px 24px rgba(230,115,0,0.15)`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = BORDER;
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: `radial-gradient(circle at 50% 0%, oklch(0.66 0.19 42 / 0.05) 0%, transparent 60%)`,
                    }}
                  />
                  <div className="relative z-10 w-full">
                    <div
                      className="w-16 h-16 rounded flex items-center justify-center mb-5 mx-auto"
                      style={{
                        background: 'oklch(0.66 0.19 42 / 0.12)',
                        border: '1px solid oklch(0.66 0.19 42 / 0.25)',
                      }}
                    >
                      <Icon className="w-9 h-9" style={{ color: ORANGE }} />
                    </div>
                    <span
                      className="text-[10px] font-mono font-bold tracking-[0.2em] uppercase mb-2 block"
                      style={{ color: MUTED_TEXT }}
                    >
                      {adv.titleEn}
                    </span>
                    <h3 className="text-lg font-bold text-foreground mb-3">
                      {adv.title}
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: MUTED_TEXT }}>
                      {adv.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
